<script setup lang="ts">
import BrandIcon from "../components/BrandIcon.vue";
import PageShell from "../components/PageShell.vue";

const benefits = [
	{
		icon: "flying",
		title: "Every flight, debriefed",
		body: "Your Garmin avionics send us the log the moment you're on the ground. We score your approach and touchdown against your aircraft's own performance tables, and your debrief lands minutes after shutdown.",
	},
	{
		icon: "approach",
		title: "A safety program, not a dashboard",
		body: "Exceedances, stabilized-approach rates and trends roll up across your aircraft, so you see what's drifting long before it becomes an event.",
	},
	{
		icon: "handshake",
		title: "Coverage that reflects how you fly",
		body: "Class A writes aviation insurance. Operators running a real FOQA program carry measurably less risk — and we'd rather price the flying than the paperwork.",
	},
] as const;

const GARMIN_GUIDE_URL = "https://support.garmin.com/en-US/?faq=pyAKALrPsY7ceSdP4iFT19";
const GARMIN_COMPATIBLE_URL = "https://www.garmin.com/FlightLogs";

const steps = [
	{
		title: "Open flight log sharing",
		body: "Sign in at fly.garmin.com and open Aircraft Flight Log Sharing under the Logbook tab.",
	},
	{
		title: "Choose Class A",
		body: "Find Class A in the list of available providers and select Connect Account.",
	},
	{
		title: "Sign in with us",
		body: "Garmin hands you to this site. Sign in with your Class A account, or create one and our team will review it.",
	},
	{
		title: "Confirm on flyGarmin",
		body: "We send you straight back to flyGarmin, which finalizes the connection.",
	},
	{
		title: "Pick your aircraft",
		body: "Authorize each aircraft you want on the program. Once your account is approved, the logs flow by themselves.",
	},
] as const;
</script>

<template>
  <PageShell>
    <h1 class="font-serif text-2xl sm:text-4xl md:text-5xl leading-tight text-center max-w-3xl mx-auto text-brand-500">
      Connect Garmin.
      <!-- Block so the two beats always land on their own lines instead of orphaning "Sky." -->
      <span class="block italic underline decoration-primary-500 decoration-4 underline-offset-4">Unlock a Smarter Sky.</span>
    </h1>
    <p class="mt-6 text-lg sm:text-xl text-brand-300 text-center max-w-2xl mx-auto">
      Every flight flows straight from your compatible Garmin avionics into your Class A
      FOQA program. No card to pull, nothing to remember.
    </p>

    <div class="mt-12 flex justify-center">
      <div class="bg-white border border-gray-200 px-6 py-5 max-w-xl w-full">
        <p class="text-brand-500">
          Ready to connect? Open the third-party sharing page in your
          <span class="font-medium">flyGarmin</span> account and choose Class A.
          <a
            href="#how-to-connect"
            class="underline decoration-primary-500 decoration-2 underline-offset-2 hover:text-brand-300"
          >See the steps.</a>
        </p>
        <p class="mt-3 text-sm text-brand-300">
          Automatic flight logging needs
          <a
            :href="GARMIN_COMPATIBLE_URL"
            target="_blank"
            rel="noopener"
            class="underline decoration-primary-500 decoration-2 underline-offset-2 hover:text-brand-500"
          >compatible Garmin avionics</a>.
        </p>
        <p class="mt-3 text-sm text-brand-300">
          Not flying with us yet?
          <a
            href="mailto:info@classa.ai"
            class="underline decoration-primary-500 decoration-2 underline-offset-2 hover:text-brand-500"
          >Talk to us</a>
          about putting your aircraft on the program.
        </p>
      </div>
    </div>

    <template #wide>
      <section class="bg-gray-900 text-white py-14 sm:py-20 px-4 sm:px-6">
        <div class="mx-auto max-w-5xl">
          <h2 class="font-serif text-2xl sm:text-3xl mb-10 underline decoration-primary-500 decoration-2 underline-offset-8">
            What you unlock
          </h2>
          <ul class="grid gap-10 sm:grid-cols-3">
            <li
              v-for="b in benefits"
              :key="b.title"
            >
              <BrandIcon
                :icon="b.icon"
                class="size-12 text-primary-500 mb-4"
              />
              <h3 class="font-medium text-lg mb-2">
                {{ b.title }}
              </h3>
              <p class="text-gray-300 leading-relaxed">
                {{ b.body }}
              </p>
            </li>
          </ul>
        </div>
      </section>

      <section
        id="how-to-connect"
        class="bg-gray-100 py-14 sm:py-20 px-4 sm:px-6 scroll-mt-8"
      >
        <div class="mx-auto max-w-5xl">
          <h2 class="font-serif text-2xl sm:text-3xl mb-10 text-brand-500 underline decoration-primary-500 decoration-2 underline-offset-8">
            How to connect
          </h2>
          <ol class="grid gap-8 sm:grid-cols-2 lg:grid-cols-5 max-w-md sm:max-w-none mx-auto">
            <li
              v-for="(step, i) in steps"
              :key="step.title"
            >
              <span
                class="font-cta text-4xl text-brand-500 border-b-4 border-primary-500 inline-block pb-1 mb-4"
                aria-hidden="true"
              >{{ i + 1 }}</span>
              <h3 class="font-medium text-brand-500 mb-2">
                {{ step.title }}
              </h3>
              <p class="text-brand-300 leading-relaxed text-sm">
                {{ step.body }}
              </p>
            </li>
          </ol>
          <p class="mt-10 text-sm text-brand-300">
            Prefer screenshots? Garmin's own walkthrough covers the flyGarmin side:
            <a
              :href="GARMIN_GUIDE_URL"
              target="_blank"
              rel="noopener"
              class="underline decoration-primary-500 decoration-2 underline-offset-2 hover:text-brand-500"
            >aircraft flight log sharing guide</a>.
          </p>
        </div>
      </section>
    </template>
  </PageShell>
</template>
